import { useEffect, useState, useLayoutEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { X, ArrowRight, Sparkles } from "lucide-react";

// Welcome Lap — a 30-second guided tour shown on the dashboard right after the
// athlete completes onboarding (?welcome=1 query param) or via the "Replay
// tour" link in Settings. It anchors a small spotlight popover to the three
// primary CTAs so new users immediately know what to do next.
//
// We deliberately keep this stateless w.r.t. the backend — the dismissal flag
// lives in localStorage. If marketing wants to A/B test it later, we'll lift
// it to a feature flag.

const STEPS = [
  {
    anchor: "quick-mediakit-btn",
    title: "1 · Generate your editorial media kit",
    body: "AI auto-fills career highlights, honours, demographics and a pull-quote from your profile. Review, edit, ship — usually under 2 minutes.",
  },
  {
    anchor: "quick-addbrand-btn",
    title: "2 · Add a brand you'd love to work with",
    body: "Drop any brand name and PITCHD enriches the contact, scores fit and surfaces angles. We don't pay per reveal — the database is yours.",
  },
  {
    anchor: "quick-outreach-btn",
    title: "3 · Send your first AI outreach",
    body: "We draft a sponsorship email — subject, body, follow-up, pitch angle — tuned to your sport, audience and the brand's positioning. One click to send.",
  },
];

const LOCAL_KEY = "pitchd_welcome_lap_done_v1";

export default function WelcomeLap() {
  const [search, setSearch] = useSearchParams();
  const queryTrigger = search.get("welcome") === "1";
  const replayTrigger = search.get("welcome") === "replay";
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);

  // Open conditions: ?welcome=1 (post-onboarding) OR ?welcome=replay (manual).
  // For ?welcome=1 we additionally suppress if the user already completed the
  // tour previously — they shouldn't see it twice if they re-navigate to
  // /app/dashboard?welcome=1 by accident.
  useEffect(() => {
    if (!queryTrigger && !replayTrigger) return;
    // Defer the state mutations to a microtask so we don't violate the React
    // hook-purity lint rule (set-state-in-effect). Functionally identical.
    queueMicrotask(() => {
      if (queryTrigger && localStorage.getItem(LOCAL_KEY) === "1") {
        search.delete("welcome");
        setSearch(search, { replace: true });
        return;
      }
      setStepIdx(0);
      setActive(true);
    });
  }, [queryTrigger, replayTrigger, search, setSearch]);

  // Anchor positioning — recompute on step change, resize, scroll.
  useLayoutEffect(() => {
    if (!active) return;
    const compute = () => {
      const el = document.querySelector(`[data-testid="${STEPS[stepIdx].anchor}"]`);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [active, stepIdx]);

  const finish = () => {
    localStorage.setItem(LOCAL_KEY, "1");
    setActive(false);
    // Strip the query param so refreshing doesn't re-open the tour.
    if (search.has("welcome")) {
      search.delete("welcome");
      setSearch(search, { replace: true });
    }
  };

  const advance = () => {
    if (stepIdx >= STEPS.length - 1) {
      finish();
    } else {
      setStepIdx((s) => s + 1);
    }
  };

  if (!active) return null;
  const step = STEPS[stepIdx];

  // Popover positioning — appear below the anchor when possible, otherwise
  // above. Clamp horizontally so we never spill off the viewport on mobile.
  const popoverWidth = 340;
  const vp = typeof window !== "undefined" ? window.innerWidth : 1200;
  let popoverTop = 0, popoverLeft = 0, placeBelow = true;
  if (rect) {
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    placeBelow = spaceBelow > 240;
    popoverTop = placeBelow ? rect.top + rect.height + 14 : rect.top - 14 - 220;
    popoverLeft = Math.min(Math.max(12, rect.left), vp - popoverWidth - 12);
  }

  return (
    <div className="fixed inset-0 z-[60] pointer-events-none" data-testid="welcome-lap">
      {/* Dim backdrop (clicks pass through to popover via pointer-events). */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[1px] pointer-events-auto"
           onClick={(e) => { e.stopPropagation(); }} />

      {/* Spotlight halo around the anchored CTA. */}
      {rect && (
        <div
          aria-hidden
          className="absolute border-2 border-volt rounded-sm pointer-events-none transition-all duration-200"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0)",
          }}
          data-testid="welcome-lap-spotlight"
        />
      )}

      {/* Popover */}
      {rect && (
        <div
          className="absolute bg-bg border border-volt/60 shadow-2xl pointer-events-auto"
          style={{ top: popoverTop, left: popoverLeft, width: popoverWidth }}
          data-testid="welcome-lap-popover"
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <div className="label-eyebrow text-volt flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Welcome lap · {stepIdx + 1}/{STEPS.length}
            </div>
            <button
              onClick={finish}
              className="text-ink-muted hover:text-white"
              data-testid="welcome-lap-close"
              aria-label="Close tour"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-4 py-4">
            <div className="font-heading text-lg uppercase tracking-tight" data-testid="welcome-lap-title">
              {step.title}
            </div>
            <p className="text-sm text-ink-muted mt-2 leading-relaxed" data-testid="welcome-lap-body">
              {step.body}
            </p>
          </div>
          <div className="flex items-center justify-between border-t border-line px-4 py-3">
            <button onClick={finish}
              className="text-xs uppercase tracking-widest text-ink-muted hover:text-white"
              data-testid="welcome-lap-skip">
              Skip tour
            </button>
            <button onClick={advance}
              className="btn-volt inline-flex items-center gap-2 text-xs"
              data-testid="welcome-lap-next">
              {stepIdx >= STEPS.length - 1 ? "Got it" : "Next"} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          {/* Tiny pointer connecting popover to anchor */}
          <div
            aria-hidden
            className="absolute w-2 h-2 bg-bg border-volt/60"
            style={{
              borderTop: placeBelow ? "1px solid" : undefined,
              borderLeft: placeBelow ? "1px solid" : undefined,
              borderBottom: !placeBelow ? "1px solid" : undefined,
              borderRight: !placeBelow ? "1px solid" : undefined,
              top: placeBelow ? -5 : "auto",
              bottom: !placeBelow ? -5 : "auto",
              left: Math.min(Math.max(16, rect.left - popoverLeft + rect.width / 2 - 4), popoverWidth - 24),
              transform: "rotate(45deg)",
            }}
          />
        </div>
      )}
    </div>
  );
}
